import type { IntlShape } from "react-intl";

// Variables exposées par défaut via Company#body_variables. La clé canonique
// (ex. ENTREPRISE) est ce qui est stocké dans le message ($ENTREPRISE) et ce que
// le backend interprète. L'éditeur affiche en revanche un token localisé compact
// (ex. $EMPRESA en ES) résolu via i18n (body_editor.variables.<KEY>).
export const DEFAULT_VARIABLE_KEYS = [
  "NOM",
  "ENTREPRISE",
  "SITE",
  "MAIL",
  "TEL",
  "HASHTAG",
  "CODE",
  "LANDING",
  "NEWSLETTER",
  "SPONSORLINK",
  "PROMOCODE",
  "VILLE",
  "PICTURE_URL",
  "NOTE_GOOGLE",
  "NB_AVIS_GOOGLE",
  "GENRE",
  "CARTE_DE_VISITE",
] as const;

const DEFAULT_KEY_SET = new Set<string>(DEFAULT_VARIABLE_KEYS);

// Clés jamais traduites : le token affiché doit rester strictement identique à
// la clé canonique (URL technique), parce qu'elles n'ont pas d'entrée
// body_editor.variables.<KEY> dans les locales. Les autres variables réservées et
// personnalisées ($CONNECT_CODE, $CODE_WIDIR, …) ne passent de toute façon jamais
// par l'i18n : elles ne font pas partie de DEFAULT_VARIABLE_KEYS. Le backend décide
// seul si CARTE_DE_VISITE est effectivement proposée (whitelist du groupe) — sa
// présence ici ne fait que la rendre traduisible si elle apparaît.
const NON_LOCALIZED_KEYS = new Set<string>(["PICTURE_URL"]);

export const isDefaultVariableKey = (key: string): boolean =>
  DEFAULT_KEY_SET.has(key);

// Token localisé (sans le $) pour une clé canonique. Renvoie la clé telle quelle
// pour les variables personnalisées (non traduites) et les clés non-localisables.
export const localizedToken = (intl: IntlShape, key: string): string =>
  DEFAULT_KEY_SET.has(key) && !NON_LOCALIZED_KEYS.has(key)
    ? intl.formatMessage({
      id: `body_editor.variables.${key}`,
      defaultMessage: key,
    })
    : key;

type VariableMaps = {
  // clé canonique -> token localisé (ex. ENTREPRISE -> EMPRESA)
  keyToToken: Record<string, string>;
  // token localisé -> clé canonique (ex. EMPRESA -> ENTREPRISE)
  tokenToKey: Record<string, string>;
};

// Construit les tables de correspondance pour les variables par défaut réellement
// disponibles (clés de `variables`, préfixées de $).
export const buildVariableMaps = (
  intl: IntlShape,
  variables: Record<string, string | null> | undefined,
): VariableMaps => {
  const keyToToken: Record<string, string> = {};
  const tokenToKey: Record<string, string> = {};
  Object.keys(variables || {}).forEach((variable) => {
    const key = variable.replace(/^\$/, "");
    if (!DEFAULT_KEY_SET.has(key)) return;
    const token = localizedToken(intl, key);
    keyToToken[key] = token;
    tokenToKey[token] = key;
  });
  return { keyToToken, tokenToKey };
};

const escapeRegExp = (value: string): string =>
  value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

// Construit une regex `$<alternance>` (tokens triés du plus long au plus court
// pour éviter les correspondances partielles), bornée par une absence de
// caractère de mot suivant.
const tokenRegExp = (tokens: string[]): RegExp | null => {
  if (tokens.length === 0) return null;
  const alternation = tokens
    .slice()
    .sort((a, b) => b.length - a.length)
    .map(escapeRegExp)
    .join("|");
  return new RegExp(`\\$(${alternation})(?![A-Za-z0-9_])`, "g");
};

// Remplace les clés canoniques par leur token localisé : $ENTREPRISE -> $EMPRESA
export const canonicalToDisplay = (
  text: string,
  maps: VariableMaps,
): string => {
  const re = tokenRegExp(Object.keys(maps.keyToToken));
  if (!re) return text;
  return text.replace(re, (_match, key) => `$${maps.keyToToken[key]}`);
};

// Remplace les tokens localisés par leur clé canonique : $EMPRESA -> $ENTREPRISE
export const displayToCanonical = (
  text: string,
  maps: VariableMaps,
): string => {
  const re = tokenRegExp(Object.keys(maps.tokenToKey));
  if (!re) return text;
  return text.replace(re, (_match, token) => `$${maps.tokenToKey[token]}`);
};
